import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';

const TTL = 2.0;

let tracerYellow: THREE.Texture | null = null;
let tracerRed: THREE.Texture | null = null;

/** A round glowing tracer: white-hot core fading through the tracer colour to transparent. A plain
 *  radial glow reads the same from every angle (no orientation needed) and never looks like a bar. */
function tracerTex(rgb: string): THREE.Texture {
  const S = 32, h = S / 2;
  const c = document.createElement('canvas');
  c.width = S; c.height = S;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, S, S);
  const grad = g.createRadialGradient(h, h, 0, h, h, h);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.28, `rgba(${rgb},0.95)`);
  grad.addColorStop(0.6, `rgba(${rgb},0.4)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class Bullet extends Entity {
  vel: THREE.Vector3;
  owner: 'player' | 'enemy';
  ttl = TTL;
  /** Position at the start of the current frame — used to form the sweep segment. */
  prevPosition = new THREE.Vector3();
  /** Set once an enemy bullet has registered a near-miss, so it only counts once. */
  nearMissed = false;
  /** Damage dealt on hit. MG rounds = 1; heavier projectiles (tank shells) override this. */
  damageAmount = 1;
  /** Scales the on-hit explosion. 1 = a spark; >1 = a real detonation (shells). */
  impactScale = 1;
  /** Fired by the unit the PLAYER is crewing — these rounds also harm friendlies + villages
   *  (friendly fire). AI-wingman rounds leave this false so the AI can't grief its own side. */
  friendlyFire = false;
  /** Heavy rounds (tank shells) that crater village buildings on impact. MG tracers leave it false
   *  so strafing planes don't level villages — only artillery does. */
  hitsBuildings = false;
  constructor(pos: THREE.Vector3, vel: THREE.Vector3, ownerId: number, owner: 'player' | 'enemy') {
    super('bullet');
    this.ownerId = ownerId;
    this.owner = owner;
    this.radius = 0.8;
    this.hp = 1;
    this.vel = vel;
    this.group.position.copy(pos);
    this.prevPosition.copy(pos);

    if (!tracerYellow) tracerYellow = tracerTex('255,210,80');
    if (!tracerRed) tracerRed = tracerTex('255,80,60');
    const mat = new THREE.SpriteMaterial({
      map: owner === 'player' ? tracerYellow! : tracerRed!,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });
    const s = new THREE.Sprite(mat);
    s.scale.set(1.4, 1.4, 1); // small round glow — never fills the cockpit view as a disc
    this.group.add(s);
  }

  update(ctx: EntityCtx): void {
    const dt = ctx.dt;
    this.prevPosition.copy(this.group.position);
    this.group.position.addScaledVector(this.vel, dt);
    this.ttl -= dt;
    if (this.ttl <= 0) this.dead = true;
  }
}
